// src/pages/Ergebnisse.jsx
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy } from "lucide-react";
import Badge from "../components/ui/Badge";
import TeamResultsModal from "../components/TeamResultsModal";
import StationResultsModal from "../components/StationResultsModal";
import { besteErgebnisse } from "../utils/stationsrang";
import { loadCachedTeamsAndStations } from "../utils/loadCachedData";
import useMetaWatcher from "../hooks/useMetaWatcher";
import { teamKey, resultKey } from "../utils/teamKey";
import { useAuth } from "../context/AuthContext";
import { nachRang } from "../utils/gleichstand";
import { istAusgesetzt } from "../utils/ausgesetzt";

// 🔧 Hilfsfunktion für erlaubte Gruppen
const getAllowedGroups = (station) => {
  const allowed =
    station.allow_groups ?? (station.allow_kids === false ? "Erwachsene" : "Alle");
  return {
    allowsAdults: allowed !== "Kinder",
    allowsKids: allowed !== "Erwachsene",
  };
};

export default function Ergebnisse() {
  const { user } = useAuth(); // null, wenn nicht eingeloggt
  // Die volle, interne Ansicht (Gesamtwertung vor der Freigabe) nur fuer den
  // Admin. BEHOBENES LECK: Hier stand `user` – jeder Angemeldete, also auch der
  // Betreuer-Login, sah ueber /ergebnisse die komplette Rangliste, obwohl die
  // Seite fuer Betreuer nur in der Seitenleiste ausgeblendet war. Betreuer
  // bekommen jetzt dieselbe oeffentliche Ansicht wie Besucher.
  const intern = user?.role === "admin";
  const [teams, setTeams] = useState([]);
  const [stations, setStations] = useState([]);
  const [filterCategory, setFilterCategory] = useState("Erwachsene");
  const [selectedTeam, setSelectedTeam] = useState(null);
  // "teams" = Rangliste der Teams, "stationen" = Liste der Stationen mit dem
  // jeweils Besten. Die Stationsnummer statt des Objekts, damit das offene Modal
  // bei neu geladenen Daten aktuell bleibt.
  const [ansicht, setAnsicht] = useState("teams");
  const [selectedStationNr, setSelectedStationNr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [published, setPublished] = useState(false);
  const [loadingPublic, setLoadingPublic] = useState(false);
  const [publicError, setPublicError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const firstLoadRef = useRef(true);

  // === Fortschrittsberechnung ===
  const calcProgress = useCallback(
    (type) => {
      const filteredTeams = teams.filter((t) => t.event_type === type);
      const relevantStations = stations.filter((s) => {
        if (istAusgesetzt(s, type)) return false;
        const { allowsAdults, allowsKids } = getAllowedGroups(s);
        if (type === "Kinder") return allowsKids;
        if (type === "Erwachsene") return allowsAdults;
        return true;
      });

      const totalPossible = filteredTeams.length * (relevantStations.length || 0);
      const done = filteredTeams.reduce(
        (sum, t) => sum + (t.stations_completed || 0),
        0
      );
      const percent =
        totalPossible > 0 ? Math.round((done / totalPossible) * 100) : 0;

      return { done, totalPossible, percent };
    },
    [teams, stations]
  );

  const progressAdults = useMemo(() => calcProgress("Erwachsene"), [calcProgress]);
  const progressKids = useMemo(() => calcProgress("Kinder"), [calcProgress]);

  // === Gefilterte & sortierte Teams ===
  const filteredTeams = useMemo(() => {
    return teams
      .filter((t) => t.event_type === filterCategory)
      // Zentral vergebener Platz (functions/services/rangliste.js) statt einer
      // eigenen Sortierung – sonst waere die Reihenfolge hier bei
      // Punktgleichheit eine andere als in Preisverleihung und Urkunden.
      .sort(nachRang);
  }, [teams, filterCategory]);

  // === Anzahl erlaubter Stationen ===
  const allowedStationsCount = useMemo(() => {
    return stations.filter((s) => {
      if (istAusgesetzt(s, filterCategory)) return false;
      const { allowsAdults, allowsKids } = getAllowedGroups(s);
      return filterCategory === "Kinder" ? allowsKids : allowsAdults;
    }).length;
  }, [stations, filterCategory]);

  // === Stationsansicht ===
  // Je Station der/die Beste der gewaehlten Kategorie. Kinder und Erwachsene
  // werden getrennt gewertet, deshalb zaehlen nur Ergebnisse von Teams der
  // gewaehlten Kategorie.
  const stationRows = useMemo(() => {
    const teamsDerKategorie = new Map(
      teams
        .filter((t) => t.event_type === filterCategory)
        .map((t) => [teamKey(t), t.team_name])
    );
    return stations
      .filter((s) => {
        const { allowsAdults, allowsKids } = getAllowedGroups(s);
        return filterCategory === "Kinder" ? allowsKids : allowsAdults;
      })
      .map((s) => {
        const ergebnisse = (Array.isArray(s.results) ? s.results : []).filter((r) =>
          teamsDerKategorie.has(resultKey(r))
        );
        return {
          station: s,
          ausgesetzt: istAusgesetzt(s, filterCategory),
          anzahl: ergebnisse.length,
          beste: besteErgebnisse(ergebnisse).map((r) => ({
            name: teamsDerKategorie.get(resultKey(r)) ?? r.team_name ?? "Unbekannt",
            points: r.points,
          })),
        };
      })
      .sort((a, b) => (a.station.station_number || 0) - (b.station.station_number || 0));
  }, [stations, teams, filterCategory]);

  const selectedStation = useMemo(
    () =>
      selectedStationNr == null
        ? null
        : stations.find((s) => Number(s.station_number) === Number(selectedStationNr)) ??
          null,
    [stations, selectedStationNr]
  );

  // === Top-Teams ===
  // Hervorhebungen vergleichen ueber den Bezugsschluessel, nicht ueber den
  // Namen – sonst waere die Auszeichnung nach einer Umbenennung falsch.
  const topTeamsByType = useMemo(() => {
    if (filterCategory === "Kinder") {
      return filteredTeams.slice(0, 5).map((t) => teamKey(t));
    }

    const top = {};
    const topOverall = filteredTeams.slice(0, 6).map((t) => teamKey(t));
    filteredTeams.forEach((team) => {
      if (!top[team.team_type]) top[team.team_type] = [];
      if (top[team.team_type].length < 2) top[team.team_type].push(team);
    });
    return { type: top, overall: topOverall };
  }, [filteredTeams, filterCategory]);

  // === Daten laden (authentifiziert oder öffentlich) ===
  const fetchData = useCallback(
    async (options = {}) => {
      try {
        if (intern) {
          if (firstLoadRef.current || options.reloadResults) setLoading(true);
          const { teams: teamsData, stations: stationInfos, results, fromCache } =
            await loadCachedTeamsAndStations(BACKEND_URL, navigate, options);

          console.log(
            fromCache
              ? "[Ergebnisse] Daten aus Cache geladen"
              : "[Ergebnisse] Daten frisch vom Backend geladen"
          );

          const resultsStations = results?.stations || [];
          const resultsTeams = results?.teams || [];

          // Ranking-Eintraege ueber die stabile Team-ID indizieren
          // (Rueckfall auf den Namen fuer Daten von vor der Umstellung).
          const resultsTeamsMap = resultsTeams.reduce((map, r) => {
            map[r.team_id ?? r.team_name] = r;
            return map;
          }, {});
          const resultsStationsMap = resultsStations.reduce((map, r) => {
            map[Number(r.station_number)] = r;
            return map;
          }, {});

          const mergedTeams = (teamsData || []).map((team) => {
            const result = resultsTeamsMap[teamKey(team)] || {};
            return {
              ...team,
              total_points: result.total_points ?? 0,
              age_bonus: result.age_bonus ?? 0,
              stations_completed: result.stations_completed ?? 0,
              rank: result.rank ?? null,
              rank_team_type: result.rank_team_type ?? null,
            };
          });

          const stationsWithResults = (stationInfos || []).map((si) => {
            const calcResults =
              resultsStationsMap[Number(si.station_number)]?.results ?? [];
            const baseResults = si.results ?? [];
            // Zusammenfuehrung ueber den Bezugsschluessel (team_id), Anzeige
            // ueber team_name. Vorher war der Name zugleich der Map-Schluessel.
            //
            // `baseResults` ist das Feld `results` auf dem Stations-Dokument.
            // Es stammt aus einer abgeschalteten Synchronisierung und
            // referenziert Teams ggf. noch ueber den Namen. Ohne Aufloesung auf
            // die ID landeten dieselben Teams unter zwei Schluesseln – und
            // erschienen doppelt in der Liste.
            const idVonName = new Map(
              (teamsData || []).map((t) => [t.team_name, teamKey(t)])
            );
            const schluessel = (r) => {
              const k = resultKey(r);
              return k == null ? null : idVonName.get(k) ?? k;
            };

            const calcMap = calcResults.reduce((m, r) => {
              const k = schluessel(r);
              if (k) m[k] = r;
              return m;
            }, {});
            const baseMap = baseResults.reduce((m, r) => {
              const k = schluessel(r);
              if (k) m[k] = r;
              return m;
            }, {});
            const allTeams = [
              ...new Set([...Object.keys(calcMap), ...Object.keys(baseMap)]),
            ];
            const mergedResults = allTeams.map((key) => {
              const base = baseMap[key] ?? {};
              const calc = calcMap[key] ?? {};
              return {
                team_id: calc.team_id ?? base.team_id ?? null,
                team_name:
                  calc.team_name ?? base.team_name ?? calc.team ?? base.team ?? null,
                estimate_value: base.estimate_value ?? calc.estimate_value ?? null,
                performance_score:
                  calc.performance_score ?? base.performance_score ?? null,
                points: calc.points ?? base.points ?? null,
                rank: calc.rank ?? null,
                entry_by: base.entry_by ?? calc.entry_by ?? null,
              };
            });
            return { ...si, results: mergedResults };
          });

          setTeams(mergedTeams);
          setStations(stationsWithResults);
          setLastUpdated(new Date().toLocaleTimeString("de-DE"));
          setPublished(true);
          if (firstLoadRef.current) firstLoadRef.current = false;
          setLoading(false);
          return;
        }

        // === Public path ===
        setLoadingPublic(true);
        setPublicError(null);
        const s = await fetch(`${BACKEND_URL}/public/state`).then((r) => r.json());
        if (!s.results_published) {
          setPublished(false);
          setTeams([]);
          setStations([]);
          setLoadingPublic(false);
          return;
        }
        setPublished(true);
        const data = await fetch(`${BACKEND_URL}/public/ergebnisse`).then((r) => {
          if (r.status === 403) throw new Error("Not published");
          return r.json();
        });

        const teamsData = data.teams || [];
        const stationInfos = data.stations || [];

        setTeams(teamsData);
        setStations(stationInfos);
        setLastUpdated(new Date().toLocaleTimeString("de-DE"));
        setLoading(false);
        setLoadingPublic(false);
      } catch (err) {
        console.error("[Ergebnisse] Fehler beim Laden:", err);
        if (intern) {
          if (err.message?.toLowerCase().includes("unauthorized")) {
            localStorage.removeItem("token");
            navigate("/login");
            return;
          }
          navigate("/error");
        } else {
          setPublicError("Fehler beim Laden der öffentlichen Daten.");
          setLoadingPublic(false);
        }
      }
    },
    [intern, BACKEND_URL, navigate]
  );

    // === MetaWatcher (immer aufrufen, aber intern deaktivieren) ===
    useMetaWatcher(
      intern ? BACKEND_URL : null, // deaktiviert sich intern, wenn null
      navigate,
      ({ results, teams, stations }) => {
        if (!intern) return; // nur in der internen Ansicht
        const changed = [];
        if (results) changed.push("Results");
        if (teams) changed.push("Teams");
        if (stations) changed.push("Stations");
        if (changed.length) {
          console.log(`[MetaWatcher] Änderungen erkannt: ${changed.join(", ")} → fetchData()`);
          fetchData({
            reloadResults: results,
            reloadTeams: teams,
            reloadStations: stations,
          });
        }
      }
    );


  // === Initial Load ===
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intern]);

  const typeColors = {
    Männer: "badge-team-men",
    Frauen: "badge-team-women",
    Mixed: "badge-team-mixed",
  };

  // === Public Status ===
  if (!intern && !published && !loadingPublic && !publicError) {
    return (
      <div className="p-6 text-center text-yellow-200">
        <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4 inline-block">
          Die Ergebnisse wurden noch nicht freigegeben. Bitte später erneut versuchen.
        </div>
      </div>
    );
  }

  if (!intern && loadingPublic) {
    return (
      <div className="p-6 text-center text-slate-300 opacity-80">
        Öffentliche Ergebnisse werden geladen …
      </div>
    );
  }

  if (!intern && publicError) {
    return (
      <div className="p-6 text-center text-red-200">
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 inline-block">
          {publicError}
        </div>
      </div>
    );
  }

  // === Render ===
  return (
    <div className="p-4 bg-secondary-950 text-white min-h-screen">
      {!intern && published && (
        <div className="mb-4 rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-blue-200 text-center">
          Öffentliche Ansicht – Ergebnisse sind freigegeben.
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-primary-500">Ergebnisse</h1>
        {!loading && (
          <p className="text-xs text-gray-400">
            Aktualisiert: {lastUpdated || "-"}
          </p>
        )}
      </div>

      {/* Fortschritts-Boxen nur in der internen Ansicht (Admin) */}
        {intern && (
          <div className="grid grid-cols-2 gap-4 mb-6">
            {[{ label: "Erwachsene", ...progressAdults }, { label: "Kinder", ...progressKids }].map(
              (p) => (
                <div key={p.label} className="p-4 rounded bg-secondary-900 text-white">
                  <h2 className="text-lg font-semibold mb-2">{p.label}</h2>
                  {loading ? (
                    <div className="h-3 bg-secondary-800 rounded animate-pulse" />
                  ) : (
                    <>
                      <div className="w-full bg-secondary-800 rounded h-3 mb-1">
                        <div
                          className="bg-primary-500 h-3 rounded transition-all"
                          style={{ width: `${p.percent}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-400">
                        {p.done} / {p.totalPossible} ({p.percent}%)
                      </p>
                    </>
                  )}
                </div>
              )
            )}
          </div>
        )}


      {/* Umschalter: Rangliste der Teams oder Ergebnisse je Station */}
      <div
        role="tablist"
        aria-label="Ansicht"
        className="inline-flex rounded-lg bg-secondary-900 p-1 mb-4"
      >
        {[
          ["teams", "Teams"],
          ["stationen", "Stationen"],
        ].map(([wert, label]) => (
          <button
            key={wert}
            role="tab"
            aria-selected={ansicht === wert}
            onClick={() => setAnsicht(wert)}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
              ansicht === wert
                ? "bg-secondary-700 text-white"
                : "text-gray-400 hover:text-gray-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex space-x-2 mb-4">
        {["Erwachsene", "Kinder"].map((cat) => (
          <button
            key={cat}
            onClick={() => setFilterCategory(cat)}
            disabled={loading}
            className={`px-4 py-2 rounded font-semibold transition-colors duration-200 ${
              filterCategory === cat
                ? "bg-primary-500 text-secondary-950"
                : "bg-secondary-700 text-gray-300 hover:bg-secondary-600"
            } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {cat}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="p-4 rounded bg-secondary-900 animate-pulse h-20" />
          ))}
        </div>
      ) : ansicht === "stationen" ? (
        stationRows.length === 0 ? (
          <p className="text-gray-400">Noch keine Stationen vorhanden.</p>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {stationRows.map(({ station, ausgesetzt, anzahl, beste }) => (
              <button
                key={station.station_number}
                type="button"
                onClick={() => setSelectedStationNr(station.station_number)}
                className="w-full flex items-center justify-between gap-3 text-left p-4 rounded bg-secondary-900 border border-transparent shadow cursor-pointer transition-transform duration-200 hover:translate-y-[-2px] hover:shadow-lg"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Badge className="badge badge-primary">#{station.station_number}</Badge>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate text-white">
                      {station.station_name || `Station ${station.station_number}`}
                    </p>
                    {ausgesetzt ? (
                      <p className="text-xs text-gray-400">
                        Für {filterCategory} ausgesetzt
                      </p>
                    ) : beste.length === 0 ? (
                      <p className="text-xs text-gray-400">Noch keine Ergebnisse</p>
                    ) : (
                      <p className="text-xs text-gray-300 flex items-center gap-1 min-w-0">
                        <Trophy className="text-yellow-400 w-4 h-4 flex-shrink-0" />
                        <span className="truncate">
                          {beste
                            .slice(0, 3)
                            .map((b) => b.name)
                            .join(", ")}
                          {beste.length > 3 && ` +${beste.length - 3}`}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  {!ausgesetzt && anzahl > 0 && (
                    <p className="text-xs text-gray-400">{anzahl} Teams</p>
                  )}
                  <span className="text-gray-500" aria-hidden="true">
                    ›
                  </span>
                </div>
              </button>
            ))}
          </div>
        )
      ) : filteredTeams.length === 0 ? (
        <p className="text-gray-400">Noch keine Teams vorhanden.</p>
      ) : (
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {filteredTeams.map((team, idx) => {
            let isFirst = false;
            let isSecond = false;
            let isTopOverall = false;

            if (filterCategory === "Kinder") {
              isFirst = idx === 0;
              isSecond = idx === 1;
              isTopOverall = idx < 5;
            } else {
              const topList = topTeamsByType.type[team.team_type] || [];
              isFirst = teamKey(topList[0]) === teamKey(team);
              isSecond = teamKey(topList[1]) === teamKey(team);
              isTopOverall = topTeamsByType.overall.includes(teamKey(team));
            }

            let backgroundColorClass = "bg-secondary-900 border border-transparent";
            if (isFirst) backgroundColorClass = "bg-secondary-800 border-2 border-yellow-400";
            else if (isSecond) backgroundColorClass = "bg-secondary-800 border-2 border-gray-400";
            else if (isTopOverall)
              backgroundColorClass = "bg-secondary-900 border border-primary-600";

            return (
              <div
                key={teamKey(team) ?? idx}
                onClick={() => setSelectedTeam(team)}
                className={`flex items-center justify-between p-4 rounded shadow cursor-pointer transition-transform duration-200 hover:translate-y-[-2px] hover:shadow-lg ${backgroundColorClass}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Badge className="badge badge-primary">#{team.rank ?? idx + 1}</Badge>

                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate text-white flex items-center gap-1">
                      {team.team_name}
                      {isFirst && <Trophy className="text-yellow-400 w-4 h-4" />}
                      {isSecond && <Trophy className="text-gray-400 w-4 h-4" />}
                      {isTopOverall && !isFirst && !isSecond && (
                        <Trophy className="text-primary-500 w-4 h-4" />
                      )}
                    </p>

                    <p className="text-xs text-gray-400">
                      {team.stations_completed || 0} / {allowedStationsCount} Stationen
                    </p>

                    <div className="flex flex-wrap gap-1 mt-1">
                      <Badge className="badge badge-secondary">{team.event_type}</Badge>
                      {team.team_type && (
                        <Badge
                          className={`badge ${
                            typeColors[team.team_type] ?? "badge-secondary"
                          }`}
                        >
                          {team.team_type}
                        </Badge>
                      )}
                      {team.event_type === "Kinder" && (
                        <Badge className="badge badge-progress-kids">
                          Ø {team.average_age} Jahre
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-right flex-shrink-0 ml-4">
                  <p className="font-bold text-xl text-primary-500">
                    {team.total_points ?? 0}
                  </p>
                  <p className="text-xs text-gray-400">Punkte</p>
                  {team.age_bonus > 0 && (
                    <p className="text-xs text-gray-400">Bonus: {team.age_bonus}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Das Team-Modal bleibt im Hintergrund "geoeffnet", solange die Rangliste
          einer Station daraus aufgerufen wurde: Schliessen fuehrt zurueck zum
          Team. Aus der Stationsansicht heraus ist kein Team gewaehlt. */}
      <TeamResultsModal
        open={!!selectedTeam && !selectedStation}
        onClose={() => setSelectedTeam(null)}
        team={selectedTeam}
        stations={stations}
        isPublic={!intern}
        onStationClick={setSelectedStationNr}
      />

      <StationResultsModal
        open={!!selectedStation}
        onClose={() => setSelectedStationNr(null)}
        station={selectedStation}
        teams={teams}
        startFilter={
          selectedTeam?.event_type === "Kinder" || selectedTeam?.event_type === "Erwachsene"
            ? selectedTeam.event_type
            : filterCategory
        }
        markiertesTeam={selectedTeam ? teamKey(selectedTeam) : null}
      />
    </div>
  );
}
